import { createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useState } from "react";
import { DEFAULT_ORGA, UserModel } from "../models/user.model";
import { OrganisationDto } from "../models/organisation.dto";
import { UserService } from "../services/user.service";

export interface Authentication {
  hasUser: boolean;
  getUser: () => UserModel;
  getMyself: () => Promise<void>;
  getCurrentOrganization: () => OrganisationDto;
  currentOrganization: OrganisationDto | null;
  changeOrganizationByCode: (code: string) => void;
  setOrganizationFromUrl: (code: string | undefined) => void;
}

export const UserContext = createContext<Authentication>({
  hasUser: false,
  getUser: () => {
    throw new Error();
  },
  getMyself: async () => {},
  getCurrentOrganization: () => {
    throw new Error();
  },
  currentOrganization: null,
  changeOrganizationByCode: () => {},
  setOrganizationFromUrl: () => {},
});

export const UserContextProvider: FunctionComponent<PropsWithChildren<unknown>> = ({ children }) => {
  const [user, fillUser] = useState<null | UserModel>(null);
  const [organization, changeOrganization] = useState<null | OrganisationDto>(null);

  const getMyself = useCallback(
    () =>
      UserService.getUserMe().then((userModel) => {
        fillUser(userModel);
        // NE JAMAIS définir automatiquement l'organisation ici
        // Elle sera TOUJOURS définie par OrganizationUrlHandler selon l'URL ou la première de la liste
        // Cela garantit que l'organisation de l'URL a toujours la priorité
        // L'organisation par défaut sera définie par OrganizationUrlHandler si nécessaire
      }),
    [fillUser]
  );

  const getUser = useCallback<() => UserModel>(() => {
    if (user === null) {
      throw new Error("You should call getMyself before access to user");
    } else {
      return user;
    }
  }, [user]);

  const getCurrentOrganization = useCallback<() => OrganisationDto>(() => {
    if (organization === null) {
      throw new Error("You should call getMyself before access to user");
    } else {
      return organization;
    }
  }, [organization]);

  const setOrganizationFromUrl = useCallback<(code: string | undefined) => void>(
    (code) => {
      if (user && code) {
        const orgFromUrl = user.organizationList.find((org) => org.code === code);
        if (orgFromUrl) {
          // Toujours mettre à jour l'organisation si le code de l'URL est différent
          // Cela permet d'écraser l'organisation par défaut définie par getMyself()
          changeOrganization(orgFromUrl);
        } else {
          // Si le code organisation n'existe pas dans la liste, ne pas charger les données
          // L'utilisateur devra être redirigé vers une organisation valide
          console.warn(`Organization code "${code}" not found in user's organization list`);
        }
      }
    },
    [user, changeOrganization]
  );

  const changeOrganizationByCode = useCallback<(code: string) => void>(
    (code) => {
      changeOrganization(getUser().organizationList.find((organization) => organization.code === code) ?? DEFAULT_ORGA);
    },
    [changeOrganization, getUser]
  );

  return (
    <UserContext.Provider
      value={{
        getUser,
        hasUser: user !== null,
        getMyself,
        getCurrentOrganization,
        currentOrganization: organization,
        changeOrganizationByCode,
        setOrganizationFromUrl,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
